/**
 * Auto-mapping helpers for the Bulk Import (`/import`) flow.
 *
 * The bulk importer accepts customer CSVs whose column headers may use either
 * the current export labels ("Date of Event", "Event Rate") OR the legacy
 * labels we used to export ("Date of Show", "Show Date", "Show Rate", "Rate").
 * Both header styles must auto-map to the same internal target field so that
 * existing customer spreadsheets keep importing without manual re-mapping.
 *
 * If you remove or rename a legacy alias here, the dual-header test in
 * `csv-import-mapping.test.ts` will fail — please update the test
 * intentionally rather than silently dropping support.
 */

export type ImportTargetField =
  | "invoiceNumber"
  | "accountName"
  | "contactName"
  | "city"
  | "state"
  | "region"
  | "rep"
  | "dateOfEvent"
  | "eventRate"
  | "phoneNumber"
  | "isPaid"
  | "datePaid"
  | "paymentType"
  | "paymentNotes"
  | "dateBooked"
  | "email"
  | "address"
  | "zip"
  | "bookingDetails"
  | "billingNotes"
  | "createdBy"
  | "talentName";

const normalize = (header: string): string =>
  header.toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Auto-map a list of CSV header strings to import target fields. Returns a
 * record keyed by the ORIGINAL header string. Columns we do not recognise
 * are omitted from the result (the import dialog lets the user map them
 * manually).
 */
export function autoMapImportColumns(
  columns: readonly string[],
): Record<string, ImportTargetField> {
  const mappings: Record<string, ImportTargetField> = {};

  for (const col of columns) {
    const n = normalize(col);

    if (n.includes("invoicenumber") || n === "invoice") {
      mappings[col] = "invoiceNumber";
    } else if (
      n.includes("nameofcommunity") ||
      n.includes("accountname") ||
      n.includes("community")
    ) {
      mappings[col] = "accountName";
    } else if (n.includes("contactname")) {
      mappings[col] = "contactName";
    } else if (n === "city") {
      mappings[col] = "city";
    } else if (n === "state") {
      mappings[col] = "state";
    } else if (n === "region") {
      mappings[col] = "region";
    } else if (n === "rep") {
      mappings[col] = "rep";
    } else if (
      // Current export label
      n.includes("dateofevent") ||
      n.includes("eventdate") ||
      // Legacy labels — keep these so older customer spreadsheets still import
      n.includes("dateofshow") ||
      n.includes("showdate")
    ) {
      mappings[col] = "dateOfEvent";
    } else if (
      // Current export label
      n.includes("eventrate") ||
      // Legacy labels
      n.includes("showrate") ||
      n === "rate"
    ) {
      mappings[col] = "eventRate";
    } else if (n.includes("phonenumber") || n === "phone") {
      mappings[col] = "phoneNumber";
    } else if (n.includes("joshpaid") || n === "paid") {
      mappings[col] = "isPaid";
    } else if (n.includes("datepaid")) {
      mappings[col] = "datePaid";
    } else if (
      n.includes("pymtmethod") ||
      n.includes("paymentmethod") ||
      n.includes("paymenttype")
    ) {
      mappings[col] = "paymentType";
    } else if (n.includes("momnotes") || n.includes("paymentnotes")) {
      mappings[col] = "paymentNotes";
    } else if (n.includes("datebooked")) {
      mappings[col] = "dateBooked";
    } else if (n.includes("email")) {
      mappings[col] = "email";
    } else if (n.includes("address")) {
      mappings[col] = "address";
    } else if (n === "zip" || n.includes("zipcode")) {
      mappings[col] = "zip";
    } else if (n.includes("bookingdetails")) {
      mappings[col] = "bookingDetails";
    } else if (n.includes("billingnotes")) {
      mappings[col] = "billingNotes";
    } else if (n.includes("createdby")) {
      mappings[col] = "createdBy";
    } else if (n === "talent" || n.includes("talentname")) {
      mappings[col] = "talentName";
    }
  }

  return mappings;
}

/**
 * Minimal CSV parser used by the Bulk Import upload flow. Handles quoted
 * fields with escaped quotes, BOM-stripped first headers, and both CR/LF
 * line endings. Not a fully RFC 4180-compliant parser — only intended for
 * the customer-facing import dialog.
 */
export function parseImportCsv(text: string): {
  columns: string[];
  rows: Array<Record<string, string>>;
} {
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length === 0) return { columns: [], rows: [] };

  const parseLine = (line: string): string[] => {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const headerLine =
    lines[0].charCodeAt(0) === 0xfeff ? lines[0].slice(1) : lines[0];
  const columns = parseLine(headerLine)
    .map((col) => col.trim())
    .filter((col) => col);

  const rows: Array<Record<string, string>> = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseLine(lines[i]);
    if (values.some((v) => v.trim())) {
      const row: Record<string, string> = {};
      columns.forEach((col, idx) => {
        row[col] = values[idx] || "";
      });
      rows.push(row);
    }
  }

  return { columns, rows };
}

/**
 * Project parsed CSV rows through a CSV-column → target-field mapping,
 * producing the target-keyed records that get POSTed to /api/import/bulk.
 * Unmapped target fields are simply omitted from each output record.
 */
export function projectImportRows(
  rows: ReadonlyArray<Record<string, string>>,
  mappings: Record<string, string>,
): Array<Record<string, string>> {
  return rows.map((row) => {
    const projected: Record<string, string> = {};
    for (const [csvCol, target] of Object.entries(mappings)) {
      if (target) projected[target] = row[csvCol] ?? "";
    }
    return projected;
  });
}
